﻿import $ from 'jquery';
import KeyCode from "./keycode";

declare global {
  interface JQuery {
    keyboardscrollable(options: IKeyboardScrollableOptions);
  }
}


export interface IKeyboardScrollableOptions {
  scrollIndex?: number;
  scrollableContainer?: IScrollableContainer | JQuery;
}

export class KeyboardScrollable {
  static get wireup(): string { return '[data-keyboardscrollable]'; }

  private $element: JQuery;
  private scrollableContainer: IScrollableContainer;
  private options: IKeyboardScrollableOptions;

  private DEFAULTS: IKeyboardScrollableOptions = {
    scrollIndex: -1,
    scrollableContainer: null
  };

  constructor($element: JQuery, options: IKeyboardScrollableOptions) {
    if ($element.length <= 0)
      throw new Error('$element cannot be null');
    this.$element = $element;

    this.options = $.extend({}, this.DEFAULTS, options);
    this.setScrollableContainer();
    this.$element.on('keydown.rustig.keyboardscrollable', this.onKeyDown.bind(this));
  }

  public updateScrollableContainer(options: IKeyboardScrollableOptions) {
    let container = options.scrollableContainer;
    let scrollIndex = options.scrollIndex;

    if (!options.scrollableContainer)
      throw new Error('container cannot be null');
    if (KeyboardScrollable.isJQuery(container))
      this.scrollableContainer = new ScrollableContainer(container, scrollIndex);
    else if (KeyboardScrollable.isIScrollableContainer(container))
      this.scrollableContainer = container;
    else
      throw new Error('container cannot be null');
  }

  public resetScrollIndex(): void {
    this.scrollableContainer.reset();
  }

  public getSelected(): JQuery {
    return this.scrollableContainer.getSelectedElement();
  }

  private setScrollableContainer(): void {
    if (!this.options.scrollableContainer) {
      let id = this.$element.data('keyboardscrollable');
      let $scrollableContainer = $('scrollablecontainer'.asDataSelector(id));

      if ($scrollableContainer.length <= 0)
        throw new Error('No scrollable container provided or registered');

      this.options.scrollableContainer = $scrollableContainer;
    }
    this.updateScrollableContainer(this.options);
  }

  private onKeyDown(event: any): void {
    let keycode = event.keyCode;

    if (keycode === KeyCode.Up)
      this.scrollableContainer.scrollUp();
    else if (keycode === KeyCode.Down)
      this.scrollableContainer.scrollDown();
    else if (keycode === KeyCode.Enter) {
      let $selected = this.scrollableContainer.getSelectedElement();
      this.$element.trigger('selected.rustig.keyboardscrollable', $selected);
    }
    else if (keycode === KeyCode.Escape)
      event.preventDefault();
  }

  public static isJQuery(container: IScrollableContainer | JQuery): container is JQuery {
    return (<JQuery>container).data != undefined;
  }

  public static isIScrollableContainer(container: IScrollableContainer | JQuery): container is IScrollableContainer {
    return (<IScrollableContainer>container) != undefined;
  }
}

function Plugin(option, args) {
  return this.each(function () {
    var $this = $(this);
    var data = $this.data('rustig.keyboardscrollable');
    var options = typeof option == 'object' && option;

    if (!data) {
      data = new KeyboardScrollable($this, options);
      $this.data('rustig.keyboardscrollable', data);
    }

    if (typeof option == 'string')
      data[option](args);
  });
}

(<any>$.fn).keyboardscrollable = Plugin;

export interface IScrollableContainer {
  reset(): void;
  scrollUp(): void;
  scrollDown(): void;
  getSelectedElement(): JQuery;
}

class ScrollableContainer {

  private $element: JQuery;
  private items: Array<JQuery>;
  private scrollIndex: number;

  constructor($element: JQuery, scrollIndex?: number) {
    if ($element.length <= 0)
      throw new Error('$element cannot be null');

    this.$element = $element;
    this.items = new Array<JQuery>();
    this.setItems();

    this.scrollIndex = scrollIndex > -1 ? scrollIndex : -1;
    if (this.scrollIndex > -1 && this.scrollIndex < this.items.length)
      this.items[this.scrollIndex].addClass('ks-hover');
  }

  public reset(): void {
    this.items = new Array<JQuery>();
    this.scrollIndex = -1;
    this.setItems();
  }

  public scrollUp(): void {
    if (this.scrollIndex <= 0)
      return;

    this.items[this.scrollIndex].removeClass('ks-hover');

    this.scrollIndex -= 1;
    let $itemAtScrollIndex = this.items[this.scrollIndex].addClass('ks-hover');
    this.scrollTo($itemAtScrollIndex);
  }

  public scrollDown(): void {
    if (this.scrollIndex + 1 == this.items.length)
      return;

    if (this.scrollIndex != -1)
      this.items[this.scrollIndex].removeClass('ks-hover');

    this.scrollIndex += 1;
    let $itemAtScrollIndex = this.items[this.scrollIndex].addClass('ks-hover');
    this.scrollTo($itemAtScrollIndex);
  }

  public getSelectedElement(): JQuery {
    if (this.scrollIndex == -1)
      return $();
    else
      return this.items[this.scrollIndex];
  }

  private setItems(): void {
    if (this.items.length == 0) {
      let self = this;
      let $children = self.$element.children();
      $children.map(function (index: number, domElement: HTMLElement) {
        self.items.push($(domElement));
      });
    }
  }

  private scrollTo($item: JQuery): void {
    let $parent = this.$element;

    $parent.scrollTop(
      $item.offset().top - $parent.offset().top + $parent.scrollTop()
    );
  }
}
